// Standard-Türberechtigungen der Visitor-Tags auf die Auswahl eines Besuchers
// anwenden.
//
// Jeder Tag kann in den Einstellungen Türberechtigungen hinterlegt haben (z.B.
// „Handwerker" → Werkstatt + Nebeneingang). Wird der Tag in einer Maske
// gewählt, kommen sie dazu; wird er wieder abgewählt, verschwinden sie —
// ausser ein anderer noch gewählter Tag bringt dieselbe Berechtigung mit.
//
// Von Hand ergänzte Berechtigungen bleiben immer bestehen: entfernt wird nur,
// was ausschliesslich am gerade abgewählten Tag hängt.

const permissionKey = (p) => p?.ExosID;

// Erwartet die Optionen aus `buildVisitorTagOptions()` — die sind bereits auf
// den Standort eingegrenzt, ein gleichnamiger Tag eines anderen Standorts
// bringt seine Berechtigungen also nicht mit.
const permissionsOf = (tagOptions, labels) => {
  const wanted = new Set(labels);
  const result = [];
  for (const option of tagOptions || []) {
    if (!wanted.has(option.value)) continue;
    for (const permission of option.defaultDoorPermissions || []) {
      if (permissionKey(permission)) result.push(permission);
    }
  }
  return result;
};

/**
 * Neue Türberechtigungsliste nach einer Änderung der Tag-Auswahl.
 *
 * @param {Array}  current         bisherige `door_permissions` des Besuchers
 * @param {Array}  previousLabels  vorher gewählte Tag-Labels
 * @param {Array}  nextLabels      neu gewählte Tag-Labels
 * @param {Array}  tagOptions      Optionen aus `buildVisitorTagOptions()`
 * @returns {Array} die angepasste `door_permissions`-Liste
 */
export function applyTagDoorPermissions(
  current,
  previousLabels,
  nextLabels,
  tagOptions,
) {
  const removedLabels = (previousLabels || []).filter(
    (l) => !(nextLabels || []).includes(l),
  );

  // Was die noch gewählten Tags mitbringen, darf nicht mit entfernt werden.
  const stillWanted = new Set(
    permissionsOf(tagOptions, nextLabels || []).map(permissionKey),
  );
  const toRemove = new Set(
    permissionsOf(tagOptions, removedLabels)
      .map(permissionKey)
      .filter((key) => !stillWanted.has(key)),
  );

  const kept = (current || []).filter(
    (p) => !toRemove.has(permissionKey(p)),
  );

  // Fehlende Berechtigungen der gewählten Tags ergänzen (ohne Duplikate).
  const present = new Set(kept.map(permissionKey));
  const added = [];
  for (const permission of permissionsOf(tagOptions, nextLabels || [])) {
    const key = permissionKey(permission);
    if (present.has(key)) continue;
    present.add(key);
    added.push(permission);
  }

  return [...kept, ...added];
}

/**
 * Formularzustand nach einem Standortwechsel zurücksetzen.
 *
 * Tags und Türberechtigungen gehören immer zu ihrem Standort: die Dormakaba-IDs
 * existieren am anderen Standort nicht, und ein Tag ist dort ein eigener
 * Datensatz mit eigenen Standard-Berechtigungen — auch wenn er gleich heisst.
 * Ein „Handwerker" von Standort A sagt über Standort B nichts aus. Deshalb wird
 * die Auswahl beim Wechsel verworfen statt umgedeutet; der Bearbeiter wählt am
 * neuen Standort neu, und die dortigen Standard-Berechtigungen greifen dann über
 * `applyTagDoorPermissions()`.
 *
 * @param {Object} options  `{ withDoorPermissions }` — Berechtigungen nur in
 *   Masken zurückgeben, die das Feld überhaupt führen
 * @returns {Object} `{ tag_labels: [], door_permissions?: [] }`
 */
export function resetTagsForLocation({ withDoorPermissions = false } = {}) {
  const result = { tag_labels: [] };
  if (withDoorPermissions) result.door_permissions = [];
  return result;
}
